import type { UIMessage } from "ai";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { ChatSDKError, type ErrorCode } from "./errors";
import type { ChatMessage } from "@/types";
import { formatISO } from "date-fns";
import { toPng, toBlob, toCanvas } from "html-to-image";
import { marked } from "marked";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export async function fetchWithErrorHandlers(
  input: RequestInfo | URL,
  init?: RequestInit
) {
  try {
    const response = await fetch(input, init);

    if (!response.ok) {
      const { code, cause } = await response.json();
      throw new ChatSDKError(code as ErrorCode, cause);
    }

    return response;
  } catch (error: unknown) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      throw new ChatSDKError("offline:chat");
    }

    throw error;
  }
}
export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
export function sanitizeText(text: string) {
  if (!text || typeof text !== "string") return "";
  return text.replace("<has_function_call>", "");
}
export function convertToUIMessagesM(logs: any[]): ChatMessage[] {
  const messages: ChatMessage[] = [];

  for (const log of logs) {
    const createdAt = formatISO(new Date(log.createdAt));
    const screenId = log.screen_id || "unknown";

    const userMessage: ChatMessage = {
      id: `${screenId}`,
      role: "user",
      parts: [
        {
          type: "text",
          text: log.prompt,
          url: log.promptImage,
        },
      ],
      metadata: { createdAt },
    };
    const systemMessage: ChatMessage = {
      id: `${screenId}`,
      role: "assistant",
      parts: [
        {
          type: "text",
          text: log.system_message,
        },
        { type: "data-html", data: log.html_file_detail },
      ],
      metadata: { createdAt },
    };

    const assistantMessage: ChatMessage = {
      id: `${screenId}`,
      role: "assistant",
      parts: [
        {
          type: "data-html",
          data: log.html_file_detail,
        },
      ],
      metadata: { createdAt },
    };

    messages.push(userMessage, systemMessage);
  }

  // console.log(messages,"messagesmessagesmessagesmessages",)

  return messages;
}
export function convertToUIMessages(logs: any[]): ChatMessage[] {
  const messages: ChatMessage[] = [];

  for (const log of logs) {
    const createdAt = formatISO(new Date(log.createdAt));
    const screenId = log.screen_id || "unknown";

    // 🧍 User message
    const userMessage: ChatMessage = {
      id: `${screenId}`,
      role: "user",
      parts: [
        {
          type: "text",
          text: log.prompt,
          url: log.promptImage,
        },
      ],
      metadata: { createdAt },
    };

    // 🤖 Assistant (system) message parts builder
    const parts: any[] = [];

    if (log.system_message) {
      parts.push({
        type: "text",
        text: log.system_message,
      });
    }

    if (log.html_file_detail) {
      parts.push({
        type: "data-html",
        data: log.html_file_detail,
      });
    }

    if (log.content) {
      parts.push({
        type: "tool-createDocument",
        toolCallId: screenId,
        text: log.content,
      });
    }

    // 🧠 System message
    const systemMessage: ChatMessage = {
      id: `${screenId}`,
      role: "assistant",
      parts,
      metadata: { createdAt },
    };

    // Only push system message if it has actual parts
    if (parts.length > 0) {
      messages.push(userMessage, systemMessage);
    } else {
      messages.push(userMessage);
    }
  }

  return messages;
}
export function convertToProjectUIMessages(logs: any[]): ChatMessage[] {
  const messages: ChatMessage[] = [];
  const isURL = (str: string) =>
    typeof str === "string" && /^https?:\/\//i.test(str);

  for (const log of logs) {
    const createdAt = formatISO(new Date(log.createdAt));
    const screenId = log.screen_id || "unknown";
    const userMessage: ChatMessage = {
      id: `${screenId}`,
      role: "user",
      parts: [
        {
          type: "text",
          text: log.prompt,
        },
      ],
      metadata: { createdAt },
    };
        const isChat =
      !!log.ai_response && (!log.content || log.content.trim() === "");
    const parts: any[] = [];

      if (log.ai_response && isChat) {
      parts.push({
        type: "text",
        text: log.ai_response,
      });
    }
    if (log.system_message && log.ai_response=="" ) {
      parts.push({
        type: "text",
        text: log.system_message,
      });
    }
    if (log.content) {
      if (isURL(log.content)) {
        parts.push({
          type: "data-html",
          data: log.content,
        });
      } else {
        parts.push({
          type: "tool-createDocument",
          toolCallId: screenId,
          text: log.content,
        });
      }
    }

    const systemMessage: ChatMessage = {
      id: `${screenId}`,
      role: "assistant",
      parts,
      metadata: { createdAt },
    };

    if (parts.length > 0) {
      messages.push(userMessage, systemMessage);
    } else {
      messages.push(userMessage);
    }
  }

  return messages;
}
export function getTextFromMessage(message: ChatMessage): string {
  return message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("");
}
export const handleDownload = async (
  url: string,
  fileName: string = "website.html"
) => {
  try {
    const noCacheUrl = `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;
    const response = await fetch(noCacheUrl, {
      cache: "no-store", // 🚫 prevents using old cached version
    });

    if (!response.ok) throw new Error("Failed to fetch file");
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;

    const randomNum = Math.floor(Math.random() * 1000000);
    let filenames = `varnz_webpage_${randomNum}.html`;

    link.download = filenames;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    try {
      const randomNum = Math.floor(Math.random() * 1000000);
      const blob = new Blob([url], { type: "text/html" });
      const url2 = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url2;
      a.download = `varnz_webpage_${randomNum}.html`;
      a.click();
      URL.revokeObjectURL(url2);
    } catch (err) {
      console.error("Download error:", err);
      alert("Failed to download file");
    }
  }
};
export const handleView = async (url: string) => {
  if (!url || typeof url !== "string") {
    alert("No valid HTML content to preview.");
    return;
  }
  const newWindow = window.open("", "_blank");
  if (!newWindow) {
    alert("Popup blocked. Please allow popups for this site.");
    return;
  }
  try {
    const noCacheUrl = `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;

    const response = await fetch(noCacheUrl, { cache: "no-store" });
    if (!response.ok) throw new Error("Failed to fetch HTML");

    const htmlContent = await response.text();
    if (!htmlContent.trim().startsWith("<!DOCTYPE html")) {
      alert("No valid HTML content to preview.");
      newWindow.close();
      return;
    }

    const blob = new Blob([htmlContent], { type: "text/html" });
    const previewUrl = URL.createObjectURL(blob);
    newWindow.location.href = previewUrl;
  } catch (err) {
    try {
      const blob = new Blob([url], { type: "text/html" });
      const url2 = URL.createObjectURL(blob);
      newWindow.location.href = url2;
    } catch (err) {
      newWindow.close();
      alert("Failed to open preview.");
      console.error("Preview error:", err);
    }
  }
};
export const handleCopy = async (url: string, test: string) => {
  if (!url || typeof url !== "string") {
    alert("No valid URL to copy from.");
    return;
  }
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch HTML");
    const htmlContent = await response.text();
    const textarea = document.createElement("textarea");
    textarea.value = htmlContent;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);

    alert("HTML copied to clipboard!");
  } catch (err) {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);

      alert("HTML copied to clipboard!");
    } catch (err) {
      alert("Failed to copy HTML.");
      console.error("Clipboard error:", err);
    }
  }
};
export function extractJSON(text: string): any | null {
  const src = String(text || "");

  // 1) Prefer fenced ```json blocks when present.
  const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/i;
  const match = src.match(jsonBlockRegex);
  if (match?.[1]) {
    const raw = match[1];
    try {
      const cleaned = raw
        .replace(/\/\/.*$/gm, "")
        .replace(/\/\*[\s\S]*?\*\//g, "")
        .replace(/'/g, '"')
        .replace(/,\s*([}\]])/g, "$1")
        .trim();
      return JSON.parse(cleaned);
    } catch {
      let cleaned = raw.trim();
      cleaned = cleaned.replace(/"([^"]*?)"s([^"]*?)"/g, '"$1\\"s$2"');
      cleaned = cleaned.replace(/,\s*([}\]])/g, "$1");
      try {
        return JSON.parse(cleaned);
      } catch {
        return null;
      }
    }
  }

  // 2) Fallback: parse a leading JSON object when the model outputs raw JSON first.
  // Example:
  // { "GRP1": { "name": "..." }, "GRP2": { "name": "..." } }
  // <!DOCTYPE html>...
  const s = src.trimStart();
  if (!s.startsWith("{")) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === "{") depth += 1;
    if (ch === "}") depth -= 1;
    if (depth === 0) {
      const candidate = s.slice(0, i + 1);
      try {
        return JSON.parse(candidate);
      } catch {
        return null;
      }
    }
  }

  return null;
}
export const handleDownloadCardAsImage = async (
  content: string,
  fileName: string,
  customWidth?: number,
  customHeight?: number
) => {
  try {
    if (!content) {
      alert("No content available to download");
      return;
    }

    // 🧩 Access iframe & its document
    const iframe = document.querySelector(
      'iframe[title="HTML Preview"]'
    ) as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument;
    if (!iframeDoc?.body) throw new Error("No iframe content found");

    // 🎯 Target the first <div> inside iframe <body>
    const targetDiv = iframeDoc.body.querySelector("div");
    if (!targetDiv) throw new Error("No <div> found inside iframe body");

    // 📏 Get dimensions
    const rect = targetDiv.getBoundingClientRect();
    const targetWidth = customWidth || rect.width;
    const targetHeight = customHeight || rect.height;

    // console.log("Target Size:", targetWidth, targetHeight);

    // 🧱 Create hidden wrapper in main document
    const wrapper = document.createElement("div");
    wrapper.style.width = `${targetWidth}px`;
    wrapper.style.height = `${targetHeight}px`;
    wrapper.style.display = "flex";
    wrapper.style.justifyContent = "center";
    wrapper.style.alignItems = "center";
    wrapper.style.position = "fixed";
    wrapper.style.top = "0";
    wrapper.style.left = "0";
    wrapper.style.zIndex = "-9999";

    // 🪄 Clone target div
    const clonedDiv = targetDiv.cloneNode(true) as HTMLElement;
    clonedDiv.style.width = `${targetWidth}px`;
    clonedDiv.style.height = `${targetHeight}px`;
    clonedDiv.style.boxSizing = "border-box";
    clonedDiv.style.overflow = "hidden";

    // 🧷 Deep copy computed styles
    const copyStyles = (src: HTMLElement, dest: HTMLElement) => {
      const computed = window.getComputedStyle(src);
      for (const key of computed) {
        dest.style.setProperty(key, computed.getPropertyValue(key));
      }
      Array.from(src.children).forEach((child, i) => {
        copyStyles(child as HTMLElement, dest.children[i] as HTMLElement);
      });
    };
    copyStyles(targetDiv, clonedDiv);

    // 🌈 Preserve background
    const background =
      window.getComputedStyle(targetDiv).backgroundImage ||
      window.getComputedStyle(targetDiv).backgroundColor;
    wrapper.style.background = background;

    // 🪄 Fix dynamic image CORS issue → replace external URLs with proxy
    const proxifyImages = (element: HTMLElement) => {
      element.querySelectorAll("img").forEach((img) => {
        const src = img.getAttribute("src");
        if (src && src.startsWith("http")) {
          img.setAttribute(
            "src",
            `/image-proxy/api?url=${encodeURIComponent(src)}`
          );
        }
      });
    };
    proxifyImages(clonedDiv);

    const proxifyBackgroundImages = async (element: HTMLElement) => {
      const allNodes = [element, ...Array.from(element.querySelectorAll("*"))];
      await Promise.all(
        allNodes.map(async (el) => {
          const style = window.getComputedStyle(el);
          const bg = style.backgroundImage || el.style.backgroundImage;
          if (bg && bg.startsWith("url(")) {
            const match = bg.match(/url\(["']?(.*?)["']?\)/);
            if (match && match[1]) {
              const originalUrl = match[1];
              try {
                const proxyUrl = `/image-proxy/api?url=${encodeURIComponent(
                  originalUrl
                )}`;
                const response = await fetch(proxyUrl);
                const blob = await response.blob();
                const base64 = await new Promise<string>((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.readAsDataURL(blob);
                });

                el.style.backgroundImage = `url(${base64})`;
              } catch (err) {
                console.warn(
                  "Failed to fetch background image:",
                  originalUrl,
                  err
                );
              }
            }
          }
        })
      );
    };
    await proxifyBackgroundImages(clonedDiv);

    // ✅ Render in hidden wrapper
    wrapper.appendChild(clonedDiv);
    document.body.appendChild(wrapper);

    await document.fonts.ready;

    // 🖼️ Convert to Canvas (CORS-safe)
    const canvas = await toCanvas(wrapper, {
      pixelRatio: 2,
      backgroundColor: background || "#1f0000ff",
      skipFonts: true,
      useCors: true,
    });

    // 💾 Convert canvas → blob → download
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((blob) => resolve(blob), "image/png")
    );
    document.body.removeChild(wrapper);

    if (!blob) {
      alert("Failed to generate image.");
      return;
    }

    const randomNum = Math.floor(Math.random() * 1000000);
    let filenames = `varnz_card_${randomNum}.png`;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = filenames;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);

    // console.log("✅ Card image downloaded successfully!");
  } catch (error) {
    console.error("❌ Error converting card to image:", error);
    alert("Failed to download card as image. Please try again.");
  }
};
export const downloadCardAsImage = async (
  content: string,
  fileName: string,
  customWidth?: number,
  customHeight?: number
) => {
  try {
    if (!content) {
      alert("No content available to download");
      return;
    }

    const iframe = document.querySelector(
      'iframe[title="HTML Preview"]'
    ) as HTMLIFrameElement;
    if (!iframe) throw new Error("Iframe not found");

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc?.body) throw new Error("No iframe content found");

    const card = doc.getElementById("card_maker");
    if (!card) throw new Error("No div with id 'card_maker' found");

    // Save original dimensions
    const originalWidth = card.offsetWidth;
    const originalHeight = card.offsetHeight;
    const hadMaxWidth = card.classList.contains("max-w-md");

    // Remove max-width if customWidth is provided
    if (customWidth) card.classList.remove("max-w-md");

    // Apply custom width and height directly
    if (customWidth) card.style.width = `${customWidth}px`;
    if (customHeight) card.style.height = `${customHeight}px`;

    // Capture image
    const dataUrl = await toPng(card as HTMLElement, {
      backgroundColor: null,
      pixelRatio: 2,
    });

    // Reset styles
    card.style.width = `${originalWidth}px`;
    card.style.height = `${originalHeight}px`;
    if (customWidth && hadMaxWidth) card.classList.add("max-w-md");

    const randomNum = Math.floor(Math.random() * 1000000);
    let filenames = `varnz_card_${randomNum}.png`;

    // Trigger download
    const link = document.createElement("a");
    link.download = filenames;
    link.href = dataUrl;
    link.click();
  } catch (error) {
    console.error("❌ Error downloading card:", error);
    alert("Failed to download card. Please try again.");
  }
};
export const downloadCardFromURL = async (
  content: string,
  fileName: string,
  customWidth?: number,
  customHeight?: number
) => {
  if (!content) {
    alert("No content available to download");
    return;
  }
  let targetDiv: HTMLElement | null = null;
  let htmlContent = content;
  const iframe = document.querySelector(
    ".document-preview-iframe"
  ) as HTMLIFrameElement;
  if (iframe) {
    const iframeUrl = iframe.src || iframe.contentWindow?.location?.pathname;
    if (iframeUrl === content) {
      // ✅ iframe matches requested content
      const iframeDoc = iframe.contentDocument;
      targetDiv = iframeDoc?.body.querySelector("div") as HTMLElement;
      if (!targetDiv) {
        console.warn(
          "⚠️ No <div> found inside matching iframe, will fallback to HTML fetch"
        );
        targetDiv = null;
      } else {
        // console.log("Using iframe content for download");
      }
    } else {
      // console.log("frame does not match requested content, ignoring iframe");
    }
  }
  // 🔄 If no iframe content, fallback to URL/path
  if (!targetDiv) {
    if (
      content.startsWith("http") ||
      content.startsWith("/") ||
      content.endsWith(".html")
    ) {
      try {
        const response = await fetch(content, { cache: "no-cache" });
        if (!response.ok)
          throw new Error(`Failed to fetch HTML: ${response.status}`);
        htmlContent = await response.text();
      } catch (err) {
        console.error("❌ Could not fetch HTML content:", err);
        alert("Failed to load HTML content from URL.");
        return;
      }
    }

    // Create temporary hidden iframe to render fetched HTML
    const tempIframe = document.createElement("iframe");
    tempIframe.style.position = "absolute";
    tempIframe.style.left = "-9999px";
    tempIframe.style.top = "0";
    tempIframe.style.width = "1000px";
    tempIframe.style.height = "1200px";
    tempIframe.style.border = "none";
    tempIframe.style.visibility = "hidden";
    document.body.appendChild(tempIframe);

    const tempDoc = tempIframe.contentDocument!;
    tempDoc.open();
    tempDoc.write(htmlContent);
    tempDoc.close();

    await new Promise((r) => setTimeout(r, 500));
    targetDiv = tempDoc.body.querySelector("div") || tempDoc.body;
    setTimeout(() => {
      try {
        document.body.removeChild(tempIframe);
      } catch {}
    }, 1000);
  }

  if (!targetDiv) {
    alert("No valid content found to download.");
    return;
  }

  // 🧠 Calculate size
  const rect = targetDiv.getBoundingClientRect();
  const targetWidth = customWidth || rect.width || 800;
  const targetHeight = customHeight || rect.height || 600;

  // 🧱 Wrapper for rendering
  const wrapper = document.createElement("div");
  wrapper.style.width = `${targetWidth}px`;
  wrapper.style.height = `${targetHeight}px`;
  wrapper.style.display = "flex";
  wrapper.style.justifyContent = "center";
  wrapper.style.alignItems = "center";
  wrapper.style.position = "fixed";
  wrapper.style.top = "0";
  wrapper.style.left = "0";
  wrapper.style.zIndex = "-9999";

  // 🧩 Clone target element
  const clonedDiv = targetDiv.cloneNode(true) as HTMLElement;
  clonedDiv.style.width = `${targetWidth}px`;
  clonedDiv.style.height = `${targetHeight}px`;
  clonedDiv.style.boxSizing = "border-box";
  clonedDiv.style.overflow = "hidden";

  // 🧷 Copy styles deeply
  const copyStyles = (src: HTMLElement, dest: HTMLElement) => {
    const computed = window.getComputedStyle(src);
    for (const key of computed) {
      dest.style.setProperty(key, computed.getPropertyValue(key));
    }
    Array.from(src.children).forEach((child, i) => {
      copyStyles(child as HTMLElement, dest.children[i] as HTMLElement);
    });
  };
  copyStyles(targetDiv, clonedDiv);

  // 🧠 Fix missing fonts
  const sanitizeFonts = (el: HTMLElement) => {
    const allNodes = [el, ...Array.from(el.querySelectorAll("*"))];
    allNodes.forEach((node) => {
      const element = node as HTMLElement;
      const computedFont = window.getComputedStyle(element).font;
      if (!computedFont || computedFont === "undefined") {
        element.style.font = "16px sans-serif";
      } else {
        const safeFont = computedFont.split(",")[0].split("/")[0].trim();
        element.style.font = safeFont || "16px sans-serif";
      }
    });
  };
  sanitizeFonts(wrapper);

  // 🌈 Copy background
  const background =
    window.getComputedStyle(targetDiv).backgroundImage ||
    window.getComputedStyle(targetDiv).backgroundColor;
  wrapper.style.background = background;

  // 🪄 Fix for dynamic image URLs → pass through proxy
  const proxifyImages = (element: HTMLElement) => {
    element.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src");
      if (src && src.startsWith("http")) {
        img.setAttribute(
          "src",
          `/image-proxy/api?url=${encodeURIComponent(src)}`
        );
      }
    });
  };
  proxifyImages(clonedDiv);

  const proxifyBackgroundImages = async (element: HTMLElement) => {
    const allNodes = [element, ...Array.from(element.querySelectorAll("*"))];
    await Promise.all(
      allNodes.map(async (el) => {
        const style = window.getComputedStyle(el);
        const bg = style.backgroundImage || el.style.backgroundImage;
        if (bg && bg.startsWith("url(")) {
          const match = bg.match(/url\(["']?(.*?)["']?\)/);
          if (match && match[1]) {
            const originalUrl = match[1];
            try {
              const proxyUrl = `/image-proxy/api?url=${encodeURIComponent(
                originalUrl
              )}`;
              const response = await fetch(proxyUrl);
              const blob = await response.blob();
              const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });

              el.style.backgroundImage = `url(${base64})`;
            } catch (err) {
              console.warn(
                "Failed to fetch background image:",
                originalUrl,
                err
              );
            }
          }
        }
      })
    );
  };
  await proxifyBackgroundImages(clonedDiv);

  wrapper.appendChild(clonedDiv);
  document.body.appendChild(wrapper);

  await document.fonts.ready;

  const canvas = await toCanvas(wrapper, {
    pixelRatio: 2,
    backgroundColor: background || "#ffffff",
    skipFonts: true,
    useCors: true,
  });

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/png")
  );
  document.body.removeChild(wrapper);

  if (!blob) {
    alert("Failed to generate image.");
    return;
  }

  const randomNum = Math.floor(Math.random() * 1000000);
  let filenames = `varnz_card_${randomNum}.png`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.download = filenames;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
};
export const downloadPDF = async (content: string) => {
  const apiUrl = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/generate-pdf`;
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ html: marked.parse(content) }),
  });

  if (!response.ok) {
    alert("Error generating PDF");
    return;
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const randomNum = Math.floor(Math.random() * 1000000);
  link.download = `varnz_doc_${randomNum}.pdf`;
  link.click();
};
export const getFileTypeLabelM = (file: File): string => {
  const type = file.type;
  const name = file.name.toLowerCase();
  const ext = name.split(".").pop() || "";

  if (type.startsWith("image/")) return "Image";
  if (type.startsWith("video/")) return "Video";
  if (type.startsWith("audio/")) return "Audio";
  if (type === "application/pdf") return "PDF";

  if (type.includes("word") || ext === "doc" || ext === "docx")
    return "Word Document";

  if (type.includes("excel") || ext === "xls" || ext === "xlsx")
    return "Excel Sheet";

  if (ext === "ppt" || ext === "pptx") return "PowerPoint";

  return "Document";
};
export const getFileTypeLabel = (file: any): string => {
  // Handle DB file object
  const name = file?.name ? file.name.toLowerCase() : "";
  const type = file?.type ? file.type.toLowerCase() : "";

  if (!name && !type) return "Unknown";

  // Determine extension
  const ext = name.split(".").pop() || "";

  if (type.startsWith("image/")) return "Image";
  if (type.startsWith("video/")) return "Video";
  if (type.startsWith("audio/")) return "Audio";
  if (ext === "pdf") return "PDF";
  if (["doc", "docx"].includes(ext)) return "Document";
  if (["zip", "rar", "7z"].includes(ext)) return "Archive";
  return ext.toUpperCase();
};
